import type { StepStatus } from '@uipath/portal-shell-types';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import UiForm from '../UiForm';
import { UiStepNavigation } from './UiStepNavigation';
import { UiStepper } from './UiStepper';
import { UiStepperProvider } from './UiStepperContextProvider';

interface StepDefinition {
    stepKey: string;
    component: React.ComponentType;
    status?: StepStatus;
}

interface UiWizardProps {
    totalSteps: number;
    steps: StepDefinition[];
    showNavigation?: boolean;
    showStepContent?: boolean;
    cancelRoute?: string;
    cancelLabel?: string;
    stepperVariant?: 'icon' | 'number';
    stepperFooter?: React.ReactNode;
    formId?: string;
    onSubmitHandler?: (data: any) => Promise<void>;
    initialStep?: number;
    /** Extra elements rendered inside the stepper provider — useful for telemetry trackers that need `useUiStepper()`. */
    children?: React.ReactNode;
}

export const UiWizard: React.FC<UiWizardProps> = ({
    totalSteps,
    steps,
    showNavigation = true,
    showStepContent = true,
    cancelRoute,
    cancelLabel,
    stepperVariant = 'number',
    stepperFooter,
    formId,
    onSubmitHandler,
    initialStep,
    children,
}) => {
    const { handleSubmit } = useFormContext();

    const formOnSubmit = useMemo(
        () => onSubmitHandler
            ? handleSubmit(onSubmitHandler)
            : undefined,
        [ handleSubmit, onSubmitHandler ]
    );

    return (
        <UiStepperProvider
            totalSteps={totalSteps}
            onSubmitHandler={onSubmitHandler}
            initialStep={initialStep}
        >
            <UiForm
                onSubmit={formOnSubmit}
                id={formId}
                dataCy="ui-wizard-form"
                disableActionsGutters
                actions={
                    showNavigation ? (
                        <UiStepNavigation
                            cancelRoute={cancelRoute}
                            cancelLabel={cancelLabel}
                        />
                    ) : undefined
                }
            >
                <UiStepper
                    steps={steps}
                    showStepContent={showStepContent}
                    stepperVariant={stepperVariant}
                    stepperFooter={stepperFooter}
                />
            </UiForm>
            {children}
        </UiStepperProvider>
    );
};
